<script lang="ts">
	import { Image } from "@unpic/svelte";
	import { Badge, Heading, Hr, Progressbar } from "flowbite-svelte-v1";
	import { cubicInOut } from "svelte/easing";
	import { fade } from "svelte/transition";
	import { twJoin, twMerge } from "tailwind-merge";
	import type { Poll, Question, QuestionStats } from "#gen/forms/v1/poll_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import { DEFAULT_ANSWER_LABEL } from "#lib/components/page/components/poll/constants.js";
	import { getShowPollBackgroundColorInResults, getShowPollBorderInResults, getShowPollTitlesInResults } from "#lib/components/page/components/poll/theme.js";
	import { transformURL } from "#lib/image.js";
	import { formatPercent } from "#lib/number.js";
	import type { JsonObject } from "@bufbuild/protobuf";

	interface Props {
		config: JsonObject | undefined;
		poll: Poll;
		question: Question;
		stats?: QuestionStats;
	}

	const { config, poll, question, stats }: Props = $props();

	let imageError = $state(false);
</script>

<div class="flex w-full flex-col gap-2">
	<Hr class="my-0" />
	{#if getShowPollTitlesInResults(config)}
		<Heading tag="h2" class={twMerge("py-2 text-left text-base font-semibold", "text-(--poll-primary-text-color)")}>{question?.name}</Heading>
	{/if}
	{#each question.pollAnswers as option, j (j)}
		{@const result = stats?.pollAnswerStats.find(r => r.pollAnswer?.value === option.value) ?? { percentage: 0 }}
		{@const selected = stats?.userPollAnswer?.value === option.value}
		{@const backgroundColor = getShowPollBackgroundColorInResults(config) ? (selected ? "bg-(--poll-user-voted-background-color)" : "bg-(--poll-results-background-color)") : ""}
		{@const borderColor = getShowPollBorderInResults(config) ? (selected ? "border border-(--poll-user-voted-border-color)" : "border border-(--poll-results-border-color)") : ""}
		{#if j !== 0 && question.pollAnswers.length === 2}
			<P size="xs" weight="semibold" class="text-(--poll-primary-text-color)" align="center">{question.alternativeText || "VS"}</P>
		{/if}
		<div class={twMerge("relative flex w-full flex-col gap-1 rounded-xl p-4", backgroundColor, borderColor)}>
			<div class="flex w-full items-start gap-4">
				<div class="flex grow items-center gap-2">
					{#if option.image?.imageUrl}
						{#if !imageError}
							<div class="h-14 w-14 flex-none rounded-md object-contain">
								<Image layout="constrained" width={72} height={72} priority={true} src={transformURL(option.image?.imageUrl)} alt={option.image?.altText} onerror={() => (imageError = true)} />
							</div>
						{:else}
							<div class={twMerge("flex h-18 w-18 flex-none items-center justify-center rounded-md border bg-gray-50 object-contain text-gray-400 transition-all duration-300 ease-out", stats && "h-14 w-14")}>
								<Icon iconSet="heroicons" name="photo" />
							</div>
						{/if}
					{/if}
					<div class="flex grow flex-col">
						<Heading tag="h3" class={twJoin("text-left text-base font-semibold", selected ? "text-(--poll-user-vote-primary-text-color)" : "text-(--poll-results-primary-text-color)", option.secondaryLabel ? "line-clamp-1" : "line-clamp-2")}>{option.primaryLabel}</Heading>
						{#if option.secondaryLabel}
							<P size="sm" class={selected ? "text-(--poll-user-vote-secondary-text-color)" : "text-(--poll-results-secondary-text-color)"}>{option.secondaryLabel}</P>
						{/if}
					</div>
				</div>
				{#if selected}
					<div transition:fade={{ duration: 300, easing: cubicInOut }} class="flex-none py-0.5">
						<Badge color="primary" class={twMerge("bg-(--poll-your-vote-background-color) text-xs font-(--poll-your-vote-font-weight) text-(--poll-your-vote-text-color) opacity-0 transition-opacity duration-300 ease-in-out", selected && "opacity-100")} style="text-align: var(--poll-your-vote-text-alignment);">{poll.yourAnswerLabel || DEFAULT_ANSWER_LABEL}</Badge>
					</div>
				{/if}
			</div>
			<div class="flex w-full items-center gap-2">
				<Progressbar animate={true} tweenDuration={500} progress={result.percentage} class={selected ? "bg-(--poll-user-voted-percentage-disabled-color)" : "bg-(--poll-results-percentage-disabled-color)"} classes={{ label: selected ? "bg-(--poll-user-voted-percentage-enabled-color)" : "bg-(--poll-results-percentage-enabled-color)" }} />
				<P size="sm" weight="semibold" class={selected ? "text-(--poll-user-vote-primary-text-color)" : "text-(--poll-results-primary-text-color)"}>{formatPercent(result.percentage)}</P>
			</div>
		</div>
	{/each}
</div>
